import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { OpensColumns } from '../../components/admin/Charts';
import { Card, Empty, Icon, Kpi, Toast, usePoll } from '../../components/admin/bits';
import { describe } from '../../components/admin/Timeline';
import { RequestItem } from '../../components/admin/Requests';
import { api, errorMessage } from '../../lib/api';
import { fmtDuration, fmtRelative } from '../../lib/format';
import type { Overview as Data, Summary } from '../../lib/types';

export const Overview = () => {
  const load = useCallback(() => Promise.all([api<Data>('/api/admin/overview'), api<{ proposals: Summary[] }>('/api/admin/proposals')]), []);
  const { data, error, reload } = usePoll(load, 15_000);
  const [toast, setToast] = useState('');
  const [grantTo, setGrantTo] = useState<Record<string, string>>({});

  if (!data) return error ? <p className="error">{error}</p> : <div className="center"><div className="spinner" /></div>;
  const [o, { proposals }] = data;
  const open = proposals.filter((p) => p.status !== 'expired' && p.status !== 'accepted');

  const grant = async (email: string, proposalId: string) => {
    try {
      await api(`/api/admin/proposals/${proposalId}/access`, { body: { patterns: [email], invite: true } });
      setToast(`Acesso liberado e convite enviado para ${email}.`);
      reload();
    } catch (err) {
      setToast(errorMessage(err));
    }
  };
  const dismiss = async (email: string) => {
    await api('/api/admin/access-requests/dismiss', { body: { email } });
    reload();
  };

  return (
    <>
      <div className="ahead">
        <div>
          <p className="kick">Visão geral</p>
          <h1>Como as propostas estão sendo lidas.</h1>
        </div>
      </div>

      <div className="kpis">
        <Kpi label="Propostas em aberto" value={o.kpis.open} sub={`${o.kpis.total} no total`} />
        <Kpi label="Aberturas em 7 dias" value={o.kpis.opens7} sub={`${o.kpis.readers7} ${o.kpis.readers7 === 1 ? 'pessoa' : 'pessoas'}`} />
        <Kpi label="Tempo de leitura em 7 dias" value={fmtDuration(o.kpis.active7)} sub="tempo ativo, aba visível" />
        <Kpi label="Aceitas em 30 dias" value={o.kpis.accepted30} />
      </div>

      <div className="grid-main">
        <div className="stack">
          <Card title="Lendo agora" aside={<span className="dim small">atualiza a cada 15 s</span>}>
            {o.live.length === 0 ? (
              <Empty>Ninguém com uma proposta aberta neste momento.</Empty>
            ) : (
              <ul className="live">
                {o.live.map((l) => (
                  <li key={l.id}>
                    <span className="live-dot" data-away={!l.visible || undefined} aria-hidden="true" />
                    <div>
                      <p>
                        <strong>{l.email}</strong> em <Link to={`/admin/propostas/${l.proposalId}`}>{l.title}</Link>
                      </p>
                      <p className="dim small">
                        {l.visible ? (l.section ? `Lendo ${l.section}` : 'Lendo') : 'Aba em segundo plano'} · {fmtDuration(l.activeMs)} ativos · rolou {l.maxScroll}% · {l.device}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Aberturas nos últimos 14 dias">
            <OpensColumns days={o.opensByDay} />
          </Card>

          <Card title="Atividade recente">
            {o.activity.length === 0 ? (
              <Empty>Quando alguém abrir uma proposta, aparece aqui.</Empty>
            ) : (
              <ol className="feed">
                {o.activity.map((a, i) => {
                  const d = describe(a);
                  return (
                    <li key={i} data-tone={d.tone}>
                      <span className="tl-ic">
                        <Icon name={d.icon} />
                      </span>
                      <div>
                        <p>
                          <strong>{a.email}</strong> · {d.text.charAt(0).toLowerCase() + d.text.slice(1)}
                        </p>
                        <p className="dim small">
                          <Link to={`/admin/propostas/${a.proposalId}`}>{a.title}</Link> · {fmtRelative(a.at)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </div>

        <div className="stack">
          <Card title="Pedidos" aside={o.pending.length > 0 ? <span className="count">{o.pending.length}</span> : undefined}>
            {o.pending.length === 0 ? (
              <Empty>Nenhum pedido em aberto.</Empty>
            ) : (
              <div className="reqs">
                {o.pending.map((r) => (
                  <RequestItem key={r.id} r={r} showProposal onChange={reload} />
                ))}
              </div>
            )}
          </Card>

          <Card title="Pedidos de acesso">
            {o.accessRequests.length === 0 ? (
              <Empty>Ninguém sem acesso tentou entrar.</Empty>
            ) : (
              <ul className="areqs">
                {o.accessRequests.map((r) => {
                  const options = [...r.suggestions, ...open.filter((p) => !r.suggestions.some((s) => s.id === p.id))];
                  const chosen = grantTo[r.email] ?? options[0]?.id ?? '';
                  return (
                    <li key={r.email}>
                      <p>
                        <strong>{r.email}</strong>
                      </p>
                      <p className="dim small">
                        tentou entrar {r.attempts > 1 ? `${r.attempts} vezes, a última ` : ''}
                        {fmtRelative(r.lastAt)}
                        {r.suggestions.length > 0 && ' · mesmo domínio de uma proposta'}
                      </p>
                      {options.length > 0 && (
                        <div className="areq-act">
                          <select className="select" aria-label={`Proposta para ${r.email}`} value={chosen} onChange={(e) => setGrantTo({ ...grantTo, [r.email]: e.target.value })}>
                            {options.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.title} · {p.clientName}
                              </option>
                            ))}
                          </select>
                          <button className="btn btn-sm" onClick={() => grant(r.email, chosen)} disabled={!chosen}>
                            Liberar
                          </button>
                        </div>
                      )}
                      <button className="btn-link small" onClick={() => dismiss(r.email)}>
                        Ignorar
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>
      {toast && <Toast text={toast} onDone={() => setToast('')} />}
    </>
  );
};
